/**
 * Estado del bloqueo de edición, sin React.
 *
 * Vive aparte para poder probarlo: la pantalla pinta lo que dice esta función, y los fallos de esta
 * clase (ofrecer edición cuando el bloqueo es de otro, o al revés) son de estado, no de pintado.
 */

import { EDIT_LOCK_WARNING_MS } from '@erp/shared';

export type LockStatus = 'idle' | 'acquiring' | 'held' | 'locked' | 'lost' | 'error' | 'free';

export interface LockOwner {
  fullName: string;
  username?: string;
  terminal?: { code: string | null; name: string | null } | null;
}

export interface LockState {
  status: LockStatus;
  owner: LockOwner | null;
  /** Nombre propio del bloqueo: mientras se tiene, la pestaña sabe cuándo caduca. */
  expiresAt: string | null;
  sameUser: boolean;
  error: string | null;
}

export const initialLockState: LockState = {
  status: 'idle', owner: null, expiresAt: null, sameUser: false, error: null
};

export function acquiring(): LockState {
  return { ...initialLockState, status: 'acquiring' };
}

export function held(expiresAt: string | null): LockState {
  return { ...initialLockState, status: 'held', expiresAt };
}

export function locked(owner: LockOwner, sameUser: boolean): LockState {
  return { ...initialLockState, status: 'locked', owner, sameUser };
}

export function lost(owner: LockOwner | null): LockState {
  return { ...initialLockState, status: 'lost', owner, error: 'Has perdido el bloqueo de edición' };
}

/**
 * El registro se ha liberado (o lo ha soltado el administrador) mientras lo mirábamos en solo
 * lectura. **No se pasa a edición solo**: la pantalla tiene el formulario cargado con los datos de
 * antes y un guardado machacaría lo que haya escrito el otro. Se avisa y se pide reabrir, que
 * vuelve a leer.
 */
export function free(): LockState {
  return { ...initialLockState, status: 'free' };
}

export function failed(message: string): LockState {
  return { ...initialLockState, status: 'error', error: message };
}

/** Se puede editar solo con el bloqueo en la mano. */
export function canEdit(state: LockState): boolean {
  return state.status === 'held';
}

/**
 * Qué se le cuenta al usuario.
 *
 * Con el bloqueo caducado (`lost`) no se oculta el formulario: se avisa de que hay cambios sin
 * guardar y se deja de ofrecer «Guardar», que es lo que evita perder lo escrito.
 */
export function lockMessage(state: LockState): { tone: 'info' | 'warning' | 'danger'; text: string } | null {
  switch (state.status) {
    case 'acquiring':
      return { tone: 'info', text: 'Comprobando si alguien lo está editando…' };
    case 'locked': {
      if (!state.owner) return { tone: 'warning', text: 'Lo está editando otro usuario: solo lectura' };
      const terminal = state.owner.terminal?.code ? ` (${state.owner.terminal.code})` : '';
      const quien = state.sameUser ? 'Lo estás editando en otra pestaña o terminal' : `Lo está editando ${state.owner.fullName}`;
      return { tone: 'warning', text: `${quien}${terminal}: solo lectura` };
    }
    case 'lost':
      return {
        tone: 'danger',
        text: state.owner
          ? `El bloqueo ha pasado a ${state.owner.fullName}. Tus cambios no se han guardado.`
          : 'El bloqueo ha caducado (o lo ha liberado un administrador). Tus cambios no se han guardado.'
      };
    case 'free':
      return { tone: 'info', text: 'Ya está libre: cierra y vuelve a abrir para editar con los datos al día.' };
    case 'error':
      return { tone: 'danger', text: state.error ?? 'No se ha podido comprobar el bloqueo' };
    default:
      return null;
  }
}

/** Solo el mismo usuario puede recuperar su propio bloqueo desde otra pestaña. */
export function canTakeOver(state: LockState): boolean {
  return state.status === 'locked' && state.sameUser;
}

/**
 * ¿Toca latir?
 *
 * Solo si ha habido actividad **después** del último latido. Latir sin actividad mantendría el
 * bloqueo vivo indefinidamente y una pestaña olvidada dejaría a los demás en solo lectura: el
 * bloqueo tiene que caducar por inactividad, que es lo que se acordó.
 */
export function shouldBeat(lastActivity: number, lastBeat: number): boolean {
  return lastActivity > lastBeat;
}

/**
 * ¿Hay que soltar la respuesta de una adquisición que llegó tarde?
 *
 * Sí, salvo que la pestaña siga editando **ese mismo** registro: entonces la respuesta vale y
 * soltarla borraría el bloqueo que se acaba de pedir (el caso de StrictMode, que monta dos veces).
 * Si el diálogo se cerró o se cambió a otro registro, la fila quedaría sin dueño quince minutos.
 */
export function shouldReleaseLate(requestResource: string, activeResource: string | null): boolean {
  return activeResource !== requestResource;
}

/** Aviso de caducidad próxima, para que no se pierda el trabajo por sorpresa. */
export function expiresSoon(state: LockState, now: Date, umbralMs = EDIT_LOCK_WARNING_MS): boolean {
  if (state.status !== 'held' || !state.expiresAt) return false;
  const restante = new Date(state.expiresAt).getTime() - now.getTime();
  return restante > 0 && restante <= umbralMs;
}

/**
 * Qué hacer con un evento de bloqueo que llega por SSE.
 *
 * Vive aquí, y no dentro del hook, porque es la regla que se rompe en silencio: si al perder el
 * bloqueo (`lock.taken`) se sigue dejando escribir, el usuario teclea en balde y descubre el
 * problema al guardar. Devuelve qué pasa y con qué dueño.
 */
export type EventDecision =
  | { action: 'none' }
  | { action: 'retry' }
  | { action: 'lost'; owner: LockOwner | null }
  | { action: 'free' }
  | { action: 'owner'; owner: LockOwner };

export function decideEvent(
  tipo: string,
  datos: Record<string, unknown> | undefined,
  estado: LockState,
  /** Identificador de esta pestaña: si el evento es suyo, no ha perdido nada. */
  holderPropio?: string | null
): EventDecision {
  const nombre = typeof datos?.by === 'string' ? datos.by : null;
  const usuario = datos?.user as { fullName?: string } | undefined;
  const terminal = (datos?.terminal ?? null) as LockOwner['terminal'];

  // Liberado: quien esperaba puede editar, pero no se le da el formulario cargado con datos viejos:
  // se le dice que lo reabra. Un guardado aquí borraría lo que haya escrito el que lo tenía.
  if (tipo === 'lock.released') {
    return ['locked', 'lost', 'error'].includes(estado.status) ? { action: 'free' } : { action: 'none' };
  }
  // Se lo ha llevado otro con «continuar aquí en esta pestaña»: quien lo tenía pierde la edición.
  // Ojo: la pestaña que toma el control recibe **su propio** evento; si el holder coincide, no pasa nada.
  if (tipo === 'lock.taken') {
    const quien = typeof datos?.holderId === 'string' ? datos.holderId : null;
    if (holderPropio && quien === holderPropio) return { action: 'none' };
    if (estado.status !== 'held' && estado.status !== 'acquiring') return { action: 'none' };
    return { action: 'lost', owner: nombre ? { fullName: nombre, terminal: null } : null };
  }
  // Liberación forzada desde Administración: quien lo tenía se entera ya (su token ya no vale).
  if (tipo === 'lock.forced') {
    if (estado.status === 'held') return { action: 'lost', owner: null };
    if (['locked', 'lost', 'error'].includes(estado.status)) return { action: 'free' };
    return { action: 'none' };
  }
  // Alguien lo ha cogido mientras mirábamos en solo lectura: se actualiza el nombre del dueño.
  if (tipo === 'lock.acquired' && estado.status === 'locked' && usuario?.fullName) {
    return { action: 'owner', owner: { fullName: usuario.fullName, terminal } };
  }
  return { action: 'none' };
}
